import { useState } from "react";
import { Link, NavLink, Outlet, useMatch } from "react-router";
import type { ModuleId } from "@wato/business-types";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Icon, Stack, type IconName } from "@wato/ui";
import { useGatedFeatures } from "../billing/useEntitlement";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { SettingsReadiness } from "./readiness/SettingsReadiness";
import { SettingsFieldResults } from "./search/SettingsFieldResults";
import { matchSettingsFields } from "./search/settings-search-index";
import { useSettingsHighlight } from "./search/useSettingsHighlight";

interface SettingsItem {
  to: string;
  label: MessageKey;
  /** Shown when the member holds one of these permissions (UI hint; API enforces). */
  anyOf?: string[];
  needsFeature?: string;
  /** OWN-332: hidden when this module is switched off. */
  module?: ModuleId;
}

interface SettingsGroup {
  id: string;
  label: MessageKey;
  icon: IconName;
  items: SettingsItem[];
}

/** OWN-361: six sections with icons. Paths are relative to `/w/:workspaceId`. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = [
  {
    id: "business",
    label: "settingsHub.b29b.groups.business",
    icon: "home",
    items: [
      { to: "settings/workspace", label: "settingsHub.general" },
      { to: "settings/workspace/hours", label: "settingsHub.b29b.nav.hours" },
      { to: "settings/workspace/modules", label: "settingsHub.b29b.nav.modules" },
      {
        to: "settings/branches",
        label: "branches.nav",
        anyOf: ["workspace.view"],
        needsFeature: "multi_branch.enabled",
      },
      { to: "settings/notifications", label: "notifications.prefs.nav" },
      {
        to: "settings/documents",
        label: "settingsHub.b29b.docs.nav",
        anyOf: ["workspace.edit"],
        module: "documents",
      },
    ],
  },
  {
    id: "team",
    label: "settingsHub.b29b.groups.team",
    icon: "users",
    items: [
      { to: "settings/team", label: "team.nav", anyOf: ["team.view"] },
      { to: "settings/roles", label: "roles.nav", anyOf: ["roles.view"] },
      { to: "settings/teams", label: "routing.nav.teams", anyOf: ["team.view"] },
      { to: "settings/approvals", label: "team.approvals.nav", anyOf: ["team.view"] },
    ],
  },
  {
    id: "ai",
    label: "settingsHub.b29b.groups.ai",
    icon: "sparkles",
    items: [
      { to: "settings/features", label: "featureSwitches.nav", anyOf: ["workspace.edit"] },
      { to: "settings/ai/behavior", label: "settingsHub.b29b.nav.aiBehavior" },
      { to: "settings/ai/byok", label: "byok.title" },
    ],
  },
  {
    id: "integrations",
    label: "settingsHub.b29b.groups.integrations",
    icon: "plug",
    items: [
      { to: "channels", label: "channels.nav", anyOf: ["channels.view"] },
      {
        to: "settings/integrations/google",
        label: "settingsHub.b29b.nav.google",
        anyOf: ["integrations.view"],
      },
      {
        to: "settings/commerce",
        label: "commerce.nav",
        anyOf: ["integrations.view"],
        needsFeature: "commerce.connectors",
      },
      {
        to: "settings/integrations/webhooks",
        label: "webhooks.title",
        anyOf: ["integrations.view"],
      },
      { to: "settings/api-keys", label: "apiKeys.nav", anyOf: ["integrations.view"] },
      {
        to: "settings/integrations/credentials",
        label: "credentialVault.nav",
        anyOf: ["integrations.view"],
      },
      {
        to: "settings/integrations/automations",
        label: "dashboard.nav.n8nTemplates",
        anyOf: ["integrations.view"],
      },
    ],
  },
  {
    id: "billing",
    label: "settingsHub.b29b.groups.billing",
    icon: "wallet",
    items: [
      { to: "settings/billing", label: "billing.nav" },
      { to: "settings/services", label: "dashboard.services.catalog.heading" },
    ],
  },
  {
    id: "security",
    label: "settingsHub.b29b.groups.security",
    icon: "lock",
    items: [
      { to: "settings/security", label: "settingsHub.b29b.nav.security" },
      { to: "settings/otp", label: "settingsHub.b29b.nav.otp", anyOf: ["integrations.view"] },
      { to: "settings/audit-log", label: "auditLog.nav", anyOf: ["audit.view"] },
    ],
  },
];

const GROUP_DESCRIPTION: Record<string, MessageKey> = {
  business: "settingsHub.b29b.groupDesc.business",
  team: "settingsHub.b29b.groupDesc.team",
  ai: "settingsHub.b29b.groupDesc.ai",
  integrations: "settingsHub.b29b.groupDesc.integrations",
  billing: "settingsHub.b29b.groupDesc.billing",
  security: "settingsHub.b29b.groupDesc.security",
};

/** `/w/:id/settings/*` — grouped sub-nav; every settings page renders in the `<Outlet />`. */
export function SettingsLayout() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const gated = useGatedFeatures(workspace.id);
  const granted = workspace.permissions.granted ?? [];
  const [query, setQuery] = useState("");
  const needle = query.trim().toLocaleLowerCase();
  const hasFieldHits = matchSettingsFields(query, (key) => t(key)).length > 0;
  useSettingsHighlight();
  const groups = SETTINGS_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (i) =>
        (!i.needsFeature || gated[i.needsFeature] !== false) &&
        (!i.module || workspace.modules?.[i.module] !== false) &&
        (!i.anyOf || i.anyOf.some((p) => granted.includes(p))) &&
        (!needle || t(i.label).toLocaleLowerCase().includes(needle)),
    ),
  })).filter((g) => g.items.length > 0);

  // Mobile: list at /settings, detail on a sub-route (HUNT-07). Desktop: side nav + detail.
  const atIndex = useMatch({ path: "/w/:workspaceId/settings", end: true }) !== null;

  if (atIndex) {
    return (
      <div className="flex flex-col gap-6">
        <header>
          <h1 className="text-xl font-semibold text-fg">{t("settingsHub.title")}</h1>
          <p className="text-sm text-fg-muted">{t("settingsHub.subtitle")}</p>
        </header>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t("settingsHub.search")}
          placeholder={t("settingsHub.search")}
          data-testid="settings-search"
          className="h-9 w-full max-w-md rounded-md max-md:h-11 border border-line bg-surface px-3 text-sm text-fg placeholder:text-fg-muted focus-visible:outline-2 focus-visible:outline-action-primary"
        />
        <SettingsFieldResults query={query} />
        {groups.length === 0 && !hasFieldHits ? (
          <p className="text-sm text-fg-muted" data-testid="settings-search-empty">
            {t("settingsHub.searchEmpty")}
          </p>
        ) : null}
        <nav aria-label={t("settingsHub.navLabel")}>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="settings-cards">
            {groups.map((g) => (
              <li
                key={g.id}
                data-testid={`settings-group-${g.id}`}
                className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-4 shadow-sm"
              >
                <h2
                  id={`settings-group-${g.id}`}
                  className="flex items-center gap-2 text-base font-semibold text-fg"
                >
                  <Icon name={g.icon} size={18} />
                  {t(g.label)}
                </h2>
                <p className="text-sm text-fg-muted">{t(GROUP_DESCRIPTION[g.id] ?? g.label)}</p>
                <ul className="mt-1 flex flex-col">
                  {g.items.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={`/w/${workspace.id}/${item.to}`}
                        className="block rounded-md px-2 py-1.5 text-sm max-md:flex max-md:min-h-11 max-md:items-center text-on-action-ghost hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-action-primary"
                      >
                        {t(item.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="hidden md:block">
        <h1 className="text-xl font-semibold text-fg">{t("settingsHub.title")}</h1>
        <p className="text-sm text-fg-muted">{t("settingsHub.subtitle")}</p>
      </header>
      {atIndex ? null : (
        <Link
          to={`/w/${workspace.id}/settings`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-on-action-ghost md:hidden"
        >
          <span aria-hidden className="rtl:-scale-x-100">
            ←
          </span>
          {t("settingsHub.back")}
        </Link>
      )}
      <div className="flex flex-col gap-6 md:flex-row">
        <nav
          aria-label={t("settingsHub.navLabel")}
          className={`shrink-0 rounded-lg bg-surface p-3 shadow-sm md:block md:w-60 ${atIndex ? "" : "hidden"}`}
        >
          <Stack gap={4}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t("settingsHub.search")}
              placeholder={t("settingsHub.search")}
              data-testid="settings-search"
              className="h-9 w-full rounded-md max-md:h-11 border border-line bg-surface px-3 text-sm text-fg placeholder:text-fg-muted focus-visible:outline-2 focus-visible:outline-action-primary"
            />
            <SettingsFieldResults query={query} />
            {groups.length === 0 && !hasFieldHits ? (
              <p className="px-2 text-sm text-fg-muted" data-testid="settings-search-empty">
                {t("settingsHub.searchEmpty")}
              </p>
            ) : null}
            {groups.map((g) => {
              return (
                <details key={g.id} open data-testid={`settings-group-${g.id}`} className="group">
                  <summary className="pointer-events-none list-none">
                    <h2
                      id={`settings-group-${g.id}`}
                      className="flex items-center gap-1.5 px-2 pb-1 text-xs font-semibold text-fg-muted"
                    >
                      <Icon name={g.icon} size={14} />
                      {t(g.label)}
                    </h2>
                  </summary>
                  <ul>
                    {g.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={`/w/${workspace.id}/${item.to}`}
                          className={({ isActive }) =>
                            isActive
                              ? "block rounded-md bg-action-ghost-hover px-2 py-2 max-md:py-3 text-sm font-semibold text-on-action-ghost"
                              : "block rounded-md px-2 py-2 max-md:py-3 text-sm text-fg-muted hover:bg-surface-sunken hover:text-fg"
                          }
                        >
                          {t(item.label)}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </details>
              );
            })}
          </Stack>
        </nav>
        <div className={`min-w-0 flex-1 ${atIndex ? "hidden md:block" : ""}`}>
          <SettingsReadiness />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** `/settings` index: on desktop a short prompt beside the nav; on mobile the nav list itself is the page. */
export function SettingsIndex() {
  const { t } = useI18n();
  return (
    <div className="rounded-lg bg-surface p-6 text-sm text-fg-muted shadow-sm">
      {t("settingsHub.pickHint")}
    </div>
  );
}
